/**
 * Stat type keys shared between frontend and backend.
 * Single source of truth for normalized stat type identifiers.
 *
 * StatKey represents the box-score-compatible internal stat keys
 * used for settlement, live stats, and scoring.
 *
 * For normalizing arbitrary strings to StatKey, use canonicalizeStatType below.
 */

export type StatKey =
  | 'pts'
  | 'reb'
  | 'ast'
  | 'threes'
  | 'blk'
  | 'stl'
  | 'turnover'
  | 'fgm'
  | 'ftm'
  // Combo stats
  | 'pra'
  | 'pr'
  | 'pa'
  | 'ra'
  | 'bs'
  // Special props
  | 'double_double'
  | 'triple_double'
  | 'first_basket'
  | 'first_team_basket'
  // Game-level (aligned with OddsAPI market keys)
  | 'spreads'
  | 'totals'
  | 'h2h'
  | 'team_totals';

/** All stat keys as a const array */
export const STAT_KEYS: readonly StatKey[] = [
  'pts',
  'reb',
  'ast',
  'threes',
  'blk',
  'stl',
  'turnover',
  'fgm',
  'ftm',
  'pra',
  'pr',
  'pa',
  'ra',
  'bs',
  'double_double',
  'triple_double',
  'first_basket',
  'first_team_basket',
  'spreads',
  'totals',
  'h2h',
  'team_totals',
] as const;

/** Check if a value is a valid StatKey */
export function isValidStatKey(value: unknown): value is StatKey {
  return typeof value === 'string' && (STAT_KEYS as readonly string[]).includes(value);
}

const MARKET_KEY_TO_STAT: Record<string, StatKey> = {
  player_points: 'pts',
  player_rebounds: 'reb',
  player_assists: 'ast',
  player_threes: 'threes',
  player_steals: 'stl',
  player_blocks: 'blk',
  player_turnovers: 'turnover',
  player_field_goals: 'fgm',
  player_frees_made: 'ftm',
  player_points_alternate: 'pts',
  player_rebounds_alternate: 'reb',
  player_assists_alternate: 'ast',
  player_threes_alternate: 'threes',
  player_steals_alternate: 'stl',
  player_blocks_alternate: 'blk',
  player_turnovers_alternate: 'turnover',
  player_field_goals_alternate: 'fgm',
  player_points_rebounds_assists: 'pra',
  player_points_rebounds: 'pr',
  player_points_assists: 'pa',
  player_rebounds_assists: 'ra',
  player_blocks_steals: 'bs',
  player_points_rebounds_assists_alternate: 'pra',
  player_points_rebounds_alternate: 'pr',
  player_points_assists_alternate: 'pa',
  player_rebounds_assists_alternate: 'ra',
  player_blocks_steals_alternate: 'bs',
  player_double_double: 'double_double',
  player_triple_double: 'triple_double',
  player_first_basket: 'first_basket',
  player_first_team_basket: 'first_team_basket',
  player_points_q1: 'pts',
  player_rebounds_q1: 'reb',
  player_assists_q1: 'ast',
};

/**
 * Convert Odds API marketKey to internal StatKey.
 * Returns null for game-level props (h2h, spreads, totals) that don't need stat lookups.
 */
export function marketKeyToStatType(marketKey: string): StatKey | null {
  return ownValue(MARKET_KEY_TO_STAT, marketKey.toLowerCase()) ?? null;
}

/** Reverse lookup: StatKey → primary Odds API market key (non-alternate) */
const STAT_TO_MARKET_KEY: Record<string, string> = {};
for (const [marketKey, statKey] of Object.entries(MARKET_KEY_TO_STAT)) {
  // Only use non-alternate, non-q1 keys as the canonical mapping
  if (
    !marketKey.includes('alternate') &&
    !marketKey.includes('_q1') &&
    !STAT_TO_MARKET_KEY[statKey]
  ) {
    STAT_TO_MARKET_KEY[statKey] = marketKey;
  }
}

/**
 * Maps known aliases (shorthand, display labels, AI output variants) to their StatKey.
 */
const STAT_TYPE_ALIASES: Record<string, StatKey> = {
  // Shorthand forms
  '3pm': 'threes',
  fg3m: 'threes',
  to: 'turnover',
  dd: 'double_double',
  td: 'triple_double',
  pts_reb_ast: 'pra',
  pts_reb: 'pr',
  pts_ast: 'pa',
  reb_ast: 'ra',
  blk_stl: 'bs',
  // Display labels
  points: 'pts',
  rebounds: 'reb',
  assists: 'ast',
  blocks: 'blk',
  steals: 'stl',
  turnovers: 'turnover',
  'field goals': 'fgm',
  'free throws': 'ftm',
  field_goals: 'fgm',
  free_throws: 'ftm',
  'double-double': 'double_double',
  'triple-double': 'triple_double',
  'first basket': 'first_basket',
  'first team basket': 'first_team_basket',
  // AI output variants
  '3-pointers': 'threes',
  three_pointers: 'threes',
  'three pointers': 'threes',
  '3s made': 'threes',
  '3pt': 'threes',
  // Combo label variants
  'blocks+steals': 'bs',
  'blk+stl': 'bs',
  'pts+reb+ast': 'pra',
  'points+rebounds+assists': 'pra',
  'pts+reb': 'pr',
  'points+rebounds': 'pr',
  'pts+ast': 'pa',
  'points+assists': 'pa',
  'reb+ast': 'ra',
  'rebounds+assists': 'ra',
  // Game-level aliases
  spread: 'spreads',
  total: 'totals',
  moneyline: 'h2h',
  team_total: 'team_totals',
  'team total': 'team_totals',
  'team totals': 'team_totals',
  money_line: 'h2h',
  // Alternate format labels ("threes+" from X+ props)
  'points+': 'pts',
  'rebounds+': 'reb',
  'assists+': 'ast',
  'threes+': 'threes',
  'pra+': 'pra',
  'points+rebounds+': 'pr',
};

const STAT_KEY_SET: ReadonlySet<string> = new Set(STAT_KEYS);

/** Multi-word and run-together spellings, matched after separators become underscores. */
const PHRASE_STAT_KEYS: Record<string, StatKey> = {
  double_double: 'double_double',
  dbldbl: 'double_double',
  triple_double: 'triple_double',
  tripledbl: 'triple_double',
  first_basket: 'first_basket',
  first_scorer: 'first_basket',
  first_team_basket: 'first_team_basket',
  field_goals: 'fgm',
  field_goals_made: 'fgm',
  fg: 'fgm',
  free_throws: 'ftm',
  free_throws_made: 'ftm',
  frees_made: 'ftm',
  ft: 'ftm',
  three_pointers: 'threes',
  three_pointers_made: 'threes',
  three_pt: 'threes',
  '3_pointer': 'threes',
  '3_pointers': 'threes',
  ml: 'h2h',
  ptsast: 'pa',
  ptast: 'pa',
  pta: 'pa',
  ppa: 'pa',
  ptsreb: 'pr',
  ptreb: 'pr',
  ppr: 'pr',
  prb: 'pr',
  preb: 'pr',
  astreb: 'ra',
  rebase: 'ra',
  rbast: 'ra',
  blkstl: 'bs',
};

/** One word of a stat label, as the stat it counts. */
const STAT_WORD_PARTS: Record<string, string> = {
  pts: 'pts',
  pt: 'pts',
  point: 'pts',
  points: 'pts',
  reb: 'reb',
  rebs: 'reb',
  rbs: 'reb',
  trb: 'reb',
  rebound: 'reb',
  rebounds: 'reb',
  ast: 'ast',
  asts: 'ast',
  assist: 'ast',
  assists: 'ast',
  blk: 'blk',
  blks: 'blk',
  block: 'blk',
  blocks: 'blk',
  stl: 'stl',
  stls: 'stl',
  steal: 'stl',
  steals: 'stl',
  to: 'tov',
  tos: 'tov',
  tov: 'tov',
  turnover: 'tov',
  turnovers: 'tov',
  fg3m: 'fg3m',
  '3pm': 'fg3m',
  '3pt': 'fg3m',
  '3p': 'fg3m',
  '3s': 'fg3m',
  three: 'fg3m',
  threes: 'fg3m',
  fgm: 'fgm',
  ftm: 'ftm',
};

/** Words that carry no stat: "pts+reb combo", "points plus". */
const FILLER_WORDS: ReadonlySet<string> = new Set([
  'combo',
  'combos',
  'combined',
  'combination',
  'comb',
  'subset',
  'plus',
]);

/** A label's stat parts, sorted and joined, as its StatKey. */
const STAT_PARTS_TO_KEY: Record<string, StatKey> = {
  pts: 'pts',
  reb: 'reb',
  ast: 'ast',
  blk: 'blk',
  stl: 'stl',
  tov: 'turnover',
  fg3m: 'threes',
  fgm: 'fgm',
  ftm: 'ftm',
  ast_pts: 'pa',
  pts_reb: 'pr',
  ast_reb: 'ra',
  ast_pts_reb: 'pra',
  blk_stl: 'bs',
};

/**
 * Any stat type spelling (alias, market key, display label, AI variant, game word) as its StatKey.
 * An unrecognized value, such as a period game key ("totals_h1"), comes back lowercased and trimmed.
 */
export function canonicalizeStatType(value: string): string {
  const lower = value.toLowerCase().trim();
  if (STAT_KEY_SET.has(lower)) return lower;
  const alias = ownValue(STAT_TYPE_ALIASES, lower);
  if (alias) return alias;

  const words = lower
    .replace(/_alternate$/, '')
    .replace(/^player_/, '')
    .split(/[\s_+\-/&,]+/)
    .filter((word) => word && !FILLER_WORDS.has(word));
  const phrase = words.join('_');
  if (STAT_KEY_SET.has(phrase)) return phrase;
  const phraseKey = ownValue(PHRASE_STAT_KEYS, phrase) ?? ownValue(STAT_TYPE_ALIASES, phrase);
  if (phraseKey) return phraseKey;

  const parts = words.map((word) => ownValue(STAT_WORD_PARTS, word));
  if (parts.length === 0 || parts.some((part) => part === undefined)) return lower;
  const key = [...new Set(parts)].sort().join('_');
  return ownValue(STAT_PARTS_TO_KEY, key) ?? lower;
}

/** A table's own entry, so input like "constructor" never reads an Object.prototype member. */
function ownValue<T>(table: Record<string, T>, key: string): T | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}

/** True when the value names a StatKey in any spelling canonicalizeStatType reads. */
export function isCanonicalStatType(value: string): boolean {
  return STAT_KEY_SET.has(canonicalizeStatType(value));
}

/** The team chart's view of a game market, or null when the stat type names no game market. */
export function gameChartPropType(statType: string): 'spread' | 'total' | 'moneyline' | null {
  const key = canonicalizeStatType(statType);
  if (key === 'spreads') return 'spread';
  if (key === 'totals') return 'total';
  if (key === 'h2h') return 'moneyline';
  return null;
}

/** The box-score, stat-average and game-log field holding a StatKey's value; threes is fg3m there. */
export function statKeyToBoxScoreField(statKey: string): string {
  return statKey === 'threes' ? 'fg3m' : statKey;
}

const COMBO_STAT_KEYS = ['pra', 'pr', 'pa', 'ra', 'bs'] as const;
export type ComboStatKey = (typeof COMBO_STAT_KEYS)[number];

/** Each combo stat's parts: the inverse of STAT_PARTS_TO_KEY's combo rows. */
export const COMBO_COMPONENTS: Readonly<Record<ComboStatKey, readonly StatKey[]>> = {
  pra: ['pts', 'reb', 'ast'],
  pr: ['pts', 'reb'],
  pa: ['pts', 'ast'],
  ra: ['reb', 'ast'],
  bs: ['blk', 'stl'],
};

function isComboStatKey(key: string): key is ComboStatKey {
  return COMBO_STAT_KEYS.some((combo) => combo === key);
}

/** The parts of a combo key spelled exactly ('pra'), or null for anything else. */
export function comboComponents(key: string): readonly StatKey[] | null {
  return isComboStatKey(key) ? COMBO_COMPONENTS[key] : null;
}

/** Box-score parts of any stat type spelling: a combo's parts, a single stat itself, else []. */
export function statKeyParts(statType: string): StatKey[] {
  const key = canonicalizeStatType(statType);
  if (isComboStatKey(key)) return [...COMBO_COMPONENTS[key]];
  const boxScoreKeys: readonly StatKey[] = [
    'pts',
    'reb',
    'ast',
    'threes',
    'blk',
    'stl',
    'turnover',
    'fgm',
    'ftm',
  ];
  return isValidStatKey(key) && boxScoreKeys.includes(key) ? [key] : [];
}

/**
 * Odds API market key for a stat type or any alias, derived from MARKET_KEY_TO_STAT so the two
 * directions cannot drift. Prop text in the "25+ pts" style marks a player market's alternate line.
 * Returns null when the value names no market.
 */
export function statTypeToMarketKey(statType: string, prop?: string | null): string | null {
  const key = canonicalizeStatType(statType);
  // Game-level StatKeys are Odds API market keys themselves
  const isGameMarket =
    key === 'spreads' || key === 'totals' || key === 'h2h' || key === 'team_totals';
  const base = isGameMarket ? key : (ownValue(STAT_TO_MARKET_KEY, key) ?? null);
  if (!base) return null;
  if (prop && /\d+\+/.test(prop) && base.startsWith('player_')) return `${base}_alternate`;
  return base;
}

/** The stat label of "Over 16.5 Points+Rebounds", canonicalized; null when not "Over/Under <line> <stat>". */
export function extractStatFromProp(prop: string | null | undefined): string | null {
  if (!prop) return null;

  // Pattern: "(Over|Under) <number> <stat_label>" — extract the stat label part
  const match = prop.match(/^(?:over|under)\s+\d+(?:\.\d+)?\s+(.+)$/i);
  if (!match?.[1]) return null;

  const label = match[1].trim();
  if (!label) return null;

  return canonicalizeStatType(label);
}

/**
 * Get the effective stat type for a leg.
 * Priority: marketKey (ground truth) → prop display string (reliable) → statType (may be stale/incorrect).
 *
 * The prop string (e.g., "Over 16.5 Points+Rebounds") is more reliable than statType because
 * it's the user-facing display value from the odds source. statType can be wrong when the AI
 * sets it inconsistently with the prop.
 */
export function getEffectiveStatType(leg: {
  marketKey?: string | null;
  statType?: string | null;
  prop?: string | null;
}): string {
  // 1. Best: marketKey from Odds API
  if (leg.marketKey) {
    const fromMarketKey = marketKeyToStatType(leg.marketKey);
    if (fromMarketKey) return fromMarketKey;
  }

  // 2. Parse prop display string for the stat label (more reliable than statType)
  const fromProp = extractStatFromProp(leg.prop);
  if (fromProp) return fromProp;

  // 3. Fallback to stored statType
  return leg.statType ? canonicalizeStatType(leg.statType) : '';
}
